import { parseClass } from "@snapcss/parser";
import { resolve } from "./resolver";
import { applyStyles, removeStyles } from "./applier";

const BREAKPOINTS: Record<string, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

interface ResponsiveEntry {
  el: Element;
  breakpoint: number;
  styles: Record<string, string>;
  styleKeys: string[];
}

const responsiveEntries: ResponsiveEntry[] = [];

export function storeResponsiveClass(el: Element, snapClasses: string[]): void {
  for (const cls of snapClasses) {
    const parsed = parseClass(cls);
    if (parsed.modifier?.type !== "responsive") continue;

    const bp = BREAKPOINTS[parsed.modifier.breakpoint];
    if (bp === undefined) continue;

    const styles = resolve({ ...parsed, modifier: null });
    if (!Object.keys(styles).length) continue;

    responsiveEntries.push({
      el,
      breakpoint: bp,
      styles,
      styleKeys: Object.keys(styles),
    });
  }
}

export function applyResponsive(): void {
  const width = window.innerWidth;

  // Group by element so we can resolve conflicts when multiple breakpoints
  // target the same CSS property (e.g. md:p-8 lg:p-[48px]).
  // Without grouping, a higher breakpoint's removeStyles call would wipe out
  // a style that a lower breakpoint had just applied.
  const byElement = new Map<Element, ResponsiveEntry[]>();
  for (const entry of responsiveEntries) {
    const list = byElement.get(entry.el) ?? [];
    list.push(entry);
    byElement.set(entry.el, list);
  }

  for (const [el, entries] of byElement) {
    // Sort ascending — lower breakpoints apply first, higher ones override (mobile-first)
    const sorted = entries.slice().sort((a, b) => a.breakpoint - b.breakpoint);

    const toApply: Record<string, string> = {};
    const allKeys = new Set<string>();

    for (const entry of sorted) {
      for (const key of entry.styleKeys) allKeys.add(key);
      if (width >= entry.breakpoint) {
        Object.assign(toApply, entry.styles);
      }
    }

    if (Object.keys(toApply).length > 0) applyStyles(el, toApply);

    const keysToRemove = [...allKeys].filter((k) => !(k in toApply));
    if (keysToRemove.length > 0) removeStyles(el, keysToRemove);
  }
}

let resizeTimer: ReturnType<typeof setTimeout> | null = null;

export function setupResizeListener(): void {
  window.addEventListener("resize", () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applyResponsive, 150);
  });
}

export function clearResponsiveEntries(): void {
  responsiveEntries.length = 0;
}
